import {
  getLockValue,
  getSettingsDataInstance,
  getShadowFabricInstance,
  unFocusObjects,
  updateBoundingBoxInfo
} from '@frontend/utils';
import { ADDITIONAL_MULTI_LINE_TEXT_FIELD_PREFIX_ID } from '@frontend/constants/templates-field-prefixes';
import { ShowPageScopeCollector } from '@frontend/group/modules/scope-collectors/show-page-collector';
import parseText from '@/common/preview/parse-text';
import { FIELD_TYPE_MULTI_LINE_TEXT } from '@frontend/constants/type-fields-of-template';
import { scaleControlsCornerSize } from '@frontend/constants/fabric-constants';
import { formDataToObject } from '@frontend/lib/utils/form-data-to-object';
import { fabric } from 'fabric';
import {getGroupNameByAddFieldId} from "@/common/preview/draw-smart-object";
import {parseJsonData} from "@frontend/services/helpers";
import {
  getRelatedElementCoords,
  processMultiRelatedFields,
  RELATED_DIRECTIONS
} from "@/common/preview/related-diagonal-line-helper";
import {
  enhanceTextWithGradient,
  applyOutlineShadow,
  waitForFont,
  waitForFontsLoaded,
} from '@/common/preview/draw-multi-line-text';
import { handleVerticalAlignment } from '@frontend/constants/alignment';

const getScopePage = () => ShowPageScopeCollector.get(['canvas_data', 'updateLayoutChanges', 'getChangesLayoutById']);

export async function drawAdditionalMultiLineText({ canvas, id, previewBoundingBoxData, event}) {
  unFocusObjects();

  if (event?.type === 'splitBulletChildren') {
    canvas.getObjects().forEach(function (o) {
      if (event?.data?.splitBulletsChildren?.ids.includes(o.element_id || null)) {
        canvas.remove(o);
      }
    })
  } else {
    canvas.getObjects().forEach(function (o) {
      if (o.id.includes(ADDITIONAL_MULTI_LINE_TEXT_FIELD_PREFIX_ID)
        && ((event && o.parent_element_id === event?.data?.parentElementId) || !event)
      ) {
        canvas.remove(o);
      }
    });
  }

  /* used to save bounding box info */
  const { canvas_data } = getScopePage();
  const formData = new FormData();
  const settingsTemplate = getSettingsDataInstance(canvas_data[id]._data?.settings_template);

  formData.append('type', ADDITIONAL_MULTI_LINE_TEXT_FIELD_PREFIX_ID);
  
  for (let index = 0; index < canvas_data[id].additionalMultiLineTexts.length; index++) {
    const field = canvas_data[id].additionalMultiLineTexts[index];
    const offset_x = canvas_data[id]._data[field + '_offset_x'] || '0';
    const offset_y = canvas_data[id]._data[field + '_offset_y'] || '0';
    const angle = canvas_data[id]._data[field + '_angle'] || '0';
    const color = canvas_data[id]._data[field + '_color'];
    const font = canvas_data[id]._data[field + '_font'] || 'Proxima-Nova-Semibold';
    const isCurved = canvas_data[id]._data[`${field}_is_curved`] || false;
    const parseTextSize = isCurved ? parseFloat : parseInt;
    const width = parseTextSize(canvas_data[id]._data[field + '_width']) || 300;
    const fontSize = parseTextSize(canvas_data[id]._data[field + '_fontsize']) || 40;
    const textAlign = canvas_data[id]._data[field + '_alignment'] || 'left';
    const lineHeight = canvas_data[id]._data[field + '_leading'] || 1;
    const charSpacing = canvas_data[id]._data[field + '_tracking'];
    let {x, y} = initCoords(canvas_data, field, id);
    const text_val = canvas_data[id]._data[field];
    let additionalStyles = canvas_data[id]._data[field + '_styles'] || [];
    let flipped = canvas_data[id]._data[`${field}_curved_flipped`] || false;
    const curvedDiameter = Number(canvas_data[id]._data[`${field}_diameter`]) || 1000;
    const maxChars = Number(canvas_data[id]._data[`${field}_max_chars`] || 10000);
    const outlineShadow = parseJsonData(canvas_data[id]._data[`${field}_custom_outline_shadow`], null)
    const gradient = parseJsonData(canvas_data[id]._data[`${field}_custom_gradient`], null)
    const multiRelatedFields = parseJsonData(canvas_data[id]._data[`${field}_parent_multi_related`] || '', {})
    const relatedField = parseJsonData(canvas_data[id]._data[`${field}_related_field`] || '', null)
    const fieldOrigin = canvas_data[id]._data[`${field}_field_origin`] || '';
    const multiRelatedParentElementId = canvas_data[id]._data[`${field}_parent_element_id`] || null
    const customBulletOptions = canvas_data[id]._data[`${field}_custom_bullet_options`] || null
    const outerGlow = canvas_data[id]._data[`${field}_outer_glow`]
      ? JSON.parse(canvas_data[id]._data[`${field}_outer_glow`])
      : null;
    const opacity = canvas_data[id]._data[field + '_opacity'] || '1';
    
    const { updateLayoutChanges } = getScopePage();
    const splitBulletsChild = canvas_data[id]._data[`${field}_split_bullets_child`] || null;
    //POR-10015
    // if (event?.type === 'splitBulletChildren') {
    //   if (!event?.data?.splitBulletsChildren?.ids?.includes(field) || !event?.data?.parentTop) {
    //     continue;
    //   }
    //
    //   // y = event.data.parentTop + splitBulletsChild.delta_top;
    // } else if (splitBulletsChild) {
    //   canvas.getObjects().forEach(function (o) {
    //     if (o.element_id === splitBulletsChild.parent_element_id) {
    //       // y = o.top + splitBulletsChild.delta_top;
    //     }
    //   });
    // }

    const curvedKerning = 1.5;
    //filter styles in case we have a broken array of styles (any empty/null values aren't allowed for fabric.styleHas method) 
    if (!Array.isArray(additionalStyles)) {
      additionalStyles = Object.values(additionalStyles).map(inner => Object.values(inner));
    }

    const isStyleObj = v => v && typeof v === 'object' && !Array.isArray(v) && Object.keys(v).length > 0;

    additionalStyles = additionalStyles
      .map(line => Array.isArray(line) ? line.filter(isStyleObj) : []);
    
    const objectId = ADDITIONAL_MULTI_LINE_TEXT_FIELD_PREFIX_ID + index;
    let fieldsOrderReassignedByUser = canvas_data[id]._data.users_fields_order;
    fieldsOrderReassignedByUser = fieldsOrderReassignedByUser ? JSON.parse(canvas_data[id]._data.users_fields_order) : false;
    const order = fieldsOrderReassignedByUser ? fieldsOrderReassignedByUser.indexOf(objectId) + 1 : 0;
    const visible = canvas_data[id]._data[field + '_visible'] ?? true;
    const name = canvas_data[id]._data[field + '_name'] 
      ? canvas_data[id]._data[field + '_name']
      : settingsTemplate.getNameById(objectId, canvas_data[id]._data[field + '_name'] || '')
      || 'Multi Line Text (add.) ' + index;
    let { styles, text } = parseText(text_val, color);
    
    if (additionalStyles && additionalStyles.length > 0) {
      styles = additionalStyles;
    }
    
    //replace inserted text with color chars to cleared text
    if (text_val && text_val !== text && text_val.includes("<c '")) {
      updateLayoutChanges(id, field, text);
      updateLayoutChanges(id, field + '_styles', styles);
    }

    if (text !== '') {
      await waitForFont(font).then(() => {});

      const fonts = new Set();

      for (const lineIndex in styles) {
        const line = styles[lineIndex];

        for (const charIndex in line) {
          const style = line[charIndex];

          if (style.fontFamily) {
            fonts.add(`${style.fontSize || parseInt(fontSize)}px ${style.fontFamily}`);
          }
        }
      }

      if (fonts.size) {
        await waitForFontsLoaded(Array.from(fonts));
      }

      const textBox = new fabric.Textbox(text, {
        id: objectId,
        element_id: field,
        parent_element_id: multiRelatedParentElementId,
        top: y + parseFloat(offset_y),
        left: x + parseFloat(offset_x),
        width: width,
        fontFamily: font,
        fontSize: fontSize,
        fill: color,
        selectable: true,
        evented: true,
        textAlign,
        opticalAlignment: [true, 1, '1', 'true'].includes(canvas_data[id]._data[`${field}_optical_alignment`]),
        lineHeight,
        charSpacing,
        fontSelector: true,
        colorSelector: true,
        fontThemeSelector: false,
        fontColor: color,
        order,
        opacity,
        name,
        link: settingsTemplate.getLinkById(objectId, ''),
        fieldType: FIELD_TYPE_MULTI_LINE_TEXT,
        fieldOrigin,
        isGeneratedBullet: [true, 1, '1', 'true'].includes(canvas_data[id]._data[`${field}_is_generated_bullet`]),
        splitBulletsChild: parseJsonData(splitBulletsChild, null),
        styles,
        shadow: getShadowFabricInstance(id, field),
        locked: getLockValue(id, field),
        angle: angle ? parseFloat(angle) : 0,
        visible,
        relatedField,
        groupName: getGroupNameByAddFieldId(
          objectId,
          canvas_data[id]
        ),
        isCurved,
        flipped,
        curvedDiameter,
        curvedKerning,
        maxChars,
        outerGlow,
        updateLayoutChanges
      });

      if (gradient) {
        textBox.set({gradientSettings: gradient});
        enhanceTextWithGradient(textBox, gradient);
      }

      if (outlineShadow?.outline) {
        applyOutlineShadow(textBox, outlineShadow);
      }

      textBox.on('changed', function () {
        this.styles = this.generateResolvedStyleArray();
      });
      
      textBox.setControlsVisibility({
        tr: true,
        br: true,
        bl: true,
        ml: true,
        mt: false,
        mr: true,
        mb: false,
        mtr: true,
      });
      
      scaleControlsCornerSize(textBox);
      // POR-9394 remove
      // textBox.set(processMultiRelated(
      //   canvas,
      //   textBox,
      //   multiRelatedFields,
      //   customBulletOptions
      // ))
      
      
      /* commenting this for now */ 

      /*
      if (relatedField && (!relatedField?.fieldType || relatedField?.fieldType !== 'Markup Field')) {
        let multiRelatedFields= [];
        const relatedFieldNameOrId = relatedField.field?.trim() || relatedField.fieldId;
        if (relatedFieldNameOrId) {
          multiRelatedFields[relatedFieldNameOrId] = {
            margin: relatedField && relatedField.margin !== undefined ? relatedField.margin : 5,
            direction: RELATED_DIRECTIONS.TOP_TO_BOTTOM_POSITION.value,
            fieldId: relatedField.fieldId,
          }
        }

        if (multiRelatedFields && typeof multiRelatedFields === 'object' && Object.keys(multiRelatedFields).length > 0) {
          textBox.set(processMultiRelated(
            canvas,
            textBox,
            multiRelatedFields,
            customBulletOptions
          ))
        }
      }
      */

      canvas.add(textBox);

      const isEnableVerticalAlignment = canvas_data[id]._data[field + '_is_enable_vertical_alignment'] ?? false;
      const fixedHeight = parseFloat(canvas_data[id]._data[field + '_container_height'])
        || parseFloat(canvas_data[id]._data[field + '_height'])
        || 0;
      if (isEnableVerticalAlignment && fixedHeight > 0) {
        const heightByContent = textBox.height;
        textBox.height = Math.max(fixedHeight, heightByContent);
        textBox.fixedHeight = fixedHeight;
        textBox.heightByContent = heightByContent;
        textBox.isSupportVerticalAlign = true;
        const verticalAlign = textAlign.split(',')[1] ? textAlign.split(',')[1] : 'top';
        const verticalOffset = handleVerticalAlignment(textBox, verticalAlign, fixedHeight - heightByContent);
        canvas_data[id]._data[field + '_vertical_offset'] = verticalOffset;
        formData.append(`${field}_vertical_offset`, verticalOffset);
        formData.append(`${field}_container_height`, fixedHeight);
      }

      const boundingBox = textBox.getBoundingRect();
      const lineHeights = Math.max(...textBox.__lineHeights);
      
      formData.append('element_ids[]', field);
      formData.append(`${field}_bounding_data`, JSON.stringify(boundingBox));
      formData.append(`${field}_width`, isCurved ? textBox._straightTextWidth : textBox.width);
      formData.append(`${field}_height`, textBox.height);
      formData.append(`${field}_line_heights`, lineHeights.toFixed(2));
    }
  }
  // UPDATE BOUNDING BOX INFO
  if (previewBoundingBoxData) {
    previewBoundingBoxData.data.push(formDataToObject(formData));
  } else {
    updateBoundingBoxInfo(formData, id);
  }
}

function processMultiRelated(canvas, textBox, multiRelatedFields, customBulletOptions) {
  let coords = {};
  if (Object.keys(multiRelatedFields).length <= 0) {
    return coords
  }

  multiRelatedFields = processMultiRelatedFields(multiRelatedFields);
  let newCoords = {};
  let eq = true;
  let i = 0;
  do {
    newCoords = {...coords};

    for (let i = 0; i < multiRelatedFields.length; i++) {
      let multiRelatedField = null;
      let modifyOptions = multiRelatedFields[i];

      canvas.getObjects().forEach((obj) => {
        //check modifyOptions?.field to handle undefined = undefined
        if (modifyOptions?.field && obj?.templateField?.name === modifyOptions?.field) {
          multiRelatedField = obj;
        } else if (modifyOptions?.fieldId && obj?.element_id === modifyOptions?.fieldId) {
          multiRelatedField = obj;
        }
      });

      newCoords = getRelatedElementCoords(
        newCoords,
        textBox,
        modifyOptions,
        multiRelatedField,
        parseFloat(customBulletOptions?.width || 0) + parseFloat(customBulletOptions?.marginLeft || 0)
      );
    }

    eq = JSON.stringify(coords) === JSON.stringify(newCoords);
    coords = {...newCoords};
    i++;
  } while(!eq && i < 5);

  return coords;
}

function initCoords(canvas_data, field, id) {
  const boundingData = parseJsonData(canvas_data[id]._data[`${field}_bounding_data`] || '{}')
  const forceBoundingData = canvas_data[id]._data[`${field}_force_bounding_data`] ?? false;

  let x = forceBoundingData ? parseFloat(boundingData?.left || 0) : 0;
  let y = forceBoundingData ? parseFloat(boundingData?.top || 0) : 0;

  return { x, y };
}
